import classNames from 'classnames'
import { Xmark as Cancel } from 'iconoir-react'
import { useEffect, useState } from 'react'
import ComboAsync from 'app/components/ui/combo/combo-async'
import type { LocationJson } from 'app/types/entities'
import { SearchResponse } from 'app/services/search'
import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'

type Props = {
  name?: string
  meta?: Record<string, number>
  location?: LocationJson
  className?: string
  placeholder?: string
  inputClassName?: string
  optionClassName?: string
  filterLocations?: { [key: string]: string }
  selectedLocations?: LocationJson[]
  disabled?: boolean
  withBlankOption?: boolean
  customIcon?: React.ReactNode
  showSearchInDropdown?: boolean
  onChange: (location: LocationJson) => void | Promise<void>
  onRemoveSelectedOption?: (location: LocationJson) => void
}

export default function LocationSelector({
  name,
  meta,
  location,
  className,
  placeholder = `Search for locations`,
  inputClassName,
  optionClassName,
  filterLocations,
  selectedLocations,
  withBlankOption = true,
  disabled,
  customIcon,
  showSearchInDropdown = true,
  onChange,
  onRemoveSelectedOption,
}: Props) {
  // const blankOption = { name: ``, id: ``, value: `` }
  const [selected, setSelected] = useState<OptionType>()
  let additionalChoices = { ...(withBlankOption && { additionalChoices: [] }) }

  const optionsMapper = (response: SearchResponse<LocationJson>): OptionType[] => {
    debug(`location`, `mapper`, response)
    return response.hits?.map((location) => ({
      id: location.id!,
      value: location.id!,
      name: location.name!,
      meta: { unread: meta?.[location.id!] },
    }))
  }
  useEffect(() => {
    if (location?.id) {
      debug(`location`, `loaded location`, location.name)
      setSelected({
        id: location.id!,
        name: location.name!,
        value: location.id!,
      })
    } else {
      setSelected(undefined)
    }
  }, [location])
  return (
    <div className={classNames(`location-selector`, className)}>
      <ComboAsync
        className="async-combo-selector"
        inputClassName={inputClassName}
        fetcher={createGqlFetcher('locations', { limit: 1000 })}
        placeholder={placeholder}
        customIcon={customIcon}
        additionalParams={filterLocations}
        name={name}
        value={selected}
        optionClassName={optionClassName}
        {...additionalChoices}
        disabled={disabled}
        onChange={onChange}
        optionsMapper={optionsMapper}
        showSearchInDropdown={showSearchInDropdown}
        chevronClassName="mr-2.5"
      />
      {selectedLocations && selectedLocations.length > 0 && (
        <div className="mt-2 flex gap-2 rounded-lg border border-default p-4">
          <div className="inline-flex flex-wrap gap-2">
            {selectedLocations?.map((option) => (
              <span
                key={option.id}
                className="flex items-baseline justify-center gap-3 rounded border border-default bg-surface-subtle p-2 text-body-sm text-strong"
              >
                {option.name}
                {!disabled && (
                  <Cancel className="h-3 w-3 hover:cursor-pointer" onClick={() => onRemoveSelectedOption?.(option)} />
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
